import { Polygon } from "geojson";
import { ClienteFincaBaseResponse } from "../../response/ClienteFincaBaseResponse";

/** Un lote del registro temporal (pasos 4 a 6 del wizard). Espeja una fila
 *  de `registro_finca.tbl_temp_lotes_finca`. */
export type RegistroFincaBorradorLote = {
  /** Id del lote en el registro temporal, o `null` si todavía no se guardó
   *  (ver `LotesTempSync`). */
  id: string | null;
  nombreLote: string;
  areaGeograficaPolygon: Polygon;
  loteOrigenId: string | null;
  /** `null` = todavía sin asignar (paso 5), distinto de `false`: asignado
   *  como no productivo. */
  isAreaProductiva: boolean | null;
  /** Cultivo de un lote productivo. */
  cultivoId: string | null;
  /** Cobertura de un lote no productivo (catálogo `LoteNoProductivo`). */
  coberturaId: string | null;
};

/** Identidad del registro de finca en curso: lo que el wizard necesita para
 *  saber QUÉ registro está llenando y en qué paso va. Los datos pesados (el
 *  área y los lotes) NO viven acá: cada paso los lee del registro temporal
 *  del servidor al montarse (`RegistroFincaTempGrpcService.leerAsync`) y los
 *  guarda ahí mismo. Nada del registro se guarda solo en memoria. */
export type RegistroFincaBorrador = {
  clienteId: string;
  /** Id del registro temporal en el servidor; `null` hasta confirmar el
   *  paso 1 (o, en modo completar, hasta el paso 2). */
  registroId: string | null;
  /** Último paso confirmado en el servidor (0 = ninguno). Decide en qué
   *  paso se retoma el wizard. */
  ultimoPaso: number;
  /** Paso 1. */
  nombre: string | null;
  /** Paso 1: 0 = Agricultura, 1 = Ganadería, `null` = sin elegir aún. */
  tipo: number | null;
  /** Paso 2: de dónde sale el área — `"draw"` (dibujada en el mapa) o
   *  `"kml"` (archivo importado). En el servidor es la `fuente` del
   *  registro. */
  aoiOrigen: "draw" | "kml" | null;
  /** Si el servidor ya tiene un área guardada para el registro. */
  tieneArea: boolean;
  /** Finca EXISTENTE a completar (sin AOI/lotes todavía — típicamente
   *  creada desde el panel "Plataforma"), o `null` en un alta nueva. */
  fincaIdACompletar: string | null;
  /** `true` = este registro está REORGANIZANDO una finca que ya existe
   *  ("Editar finca", en el menú de módulos), no creando una. El wizard abre
   *  en el paso 4 y **no deja volver a los pasos 1–3**: el área de una finca
   *  ya creada no se toca desde acá. Lo decide el servidor
   *  (`TempCrearFincaModel.EsEdicion`) y no el llamador, así que sobrevive a
   *  cerrar el modal y reabrirlo desde el recordatorio del menú. */
  esEdicion: boolean;
};

/** El registro temporal tal como está en el servidor: su identidad, el área
 *  guardada y sus lotes. */
export type RegistroTemporalLeido = {
  borrador: RegistroFincaBorrador;
  aoi: Polygon | null;
  /** Las piezas del paso 3 tal cual las dibujó (o importó) el usuario, antes
   *  de unirse en `aoi`. Es lo que el paso 3 restaura al retomarse — ver
   *  `RegistroFincaPaso3Render`. Vacío en los registros anteriores a la
   *  columna `poligonos_padre`, y mientras el registro no tenga área. */
  poligonosPadre: Polygon[];
  /** El id de cada una de esas piezas en `tbl_temp_poligonos_padre`, en la
   *  MISMA posición. Es con lo que el paso 3 le pide a GeoServer los vértices
   *  de la pieza que el usuario selecciona para editar.
   *
   *  Puede venir con cadenas vacías, o más corto que `poligonosPadre`: un
   *  registro anterior a esa tabla no tiene filas, y uno sembrado por "Editar
   *  finca" tampoco. Una pieza sin id se trata como todavía sin guardar — el
   *  primer `GuardarAoi` le asigna uno. */
  poligonosPadreIds: string[];
  lotes: RegistroFincaBorradorLote[];
  /** El archivo KML/KMZ del que salió el área, si vino de uno: el nombre que
   *  eligió el usuario y el aviso de lo que se descartó al depurarlo (los
   *  guarda `ImportarArchivoKml`). Nulos por el camino "Dibujar" y en los
   *  registros anteriores a esas columnas. */
  archivoNombre: string | null;
  archivoAvisoDepuracion: string | null;
};

export const fx_createRegistroFincaBorradorVacio = (
  clienteId: string,
): RegistroFincaBorrador => ({
  clienteId,
  registroId: null,
  ultimoPaso: 0,
  nombre: null,
  tipo: null,
  aoiOrigen: null,
  tieneArea: false,
  fincaIdACompletar: null,
  esEdicion: false,
});

/** Borrador para completar una finca EXISTENTE sin AOI/lotes (ver
 *  `fincaIdACompletar`): nombre/tipo ya vienen de la finca, así que el
 *  wizard arranca en paso 2 directo. */
export const fx_createRegistroFincaBorradorParaCompletar = (
  finca: ClienteFincaBaseResponse,
): RegistroFincaBorrador => ({
  clienteId: finca.clienteId,
  registroId: null,
  ultimoPaso: 0,
  nombre: finca.nombreFinca,
  tipo: finca.tipoFinca,
  aoiOrigen: null,
  tieneArea: false,
  fincaIdACompletar: finca.id,
  esEdicion: false,
});

/** Paso del wizard en el que se retoma un registro, según lo último que el
 *  servidor tiene confirmado. Un registro con fuente "Archivo importado" y
 *  sin área vuelve al paso 2 para volver a cargar el archivo: el perímetro
 *  se guarda al importarlo, así que esto solo pasa si el servidor lo
 *  rechazó (ej. por solape con otra finca). */
export function fx_pasoParaRetomar(borrador: RegistroFincaBorrador): number {
  // Una edición nunca vuelve antes del paso 4: se siembra con el paso 3 ya
  // confirmado (área y lotes salen de la finca) y los pasos 1–3 no aplican.
  if (borrador.esEdicion) return Math.max(borrador.ultimoPaso + 1, 4);

  switch (borrador.ultimoPaso) {
    case 0:
      return borrador.fincaIdACompletar ? 2 : 1;
    case 1:
      return 2;
    case 2:
      return borrador.aoiOrigen === "kml" && !borrador.tieneArea ? 2 : 3;
    case 3:
      return 4;
    case 4:
      return 5;
    default:
      return 6;
  }
}
